import React, { useState, useEffect } from "react";
import {
  fetchDaysAgoList,
  fetchPitchesByDaysAgo,
  fetchPitchesByCompanyName,
  updatePitchStatus,
  refreshReplyStatus,
  sendPitchFollowUp,
  deletePitch
} from "../api/pitchService";

import { refreshAllDeclarations } from '../api/declarationService';


function TablePitches() {
  const [daysAgoList, setDaysAgoList] = useState([]);
  const [selectedDays, setSelectedDays] = useState(null);
  const [pitches, setPitches] = useState([]);
  const [allPitchesMap, setAllPitchesMap] = useState({});

  const [searchTerm, setSearchTerm] = useState("");
  const [showSidebar, setShowSidebar] = useState(true);

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loadingFollowUp, setLoadingFollowUp] = useState(null);

  const [message, setMessage] = useState("");
  const [isNotificationVisible, setIsNotificationVisible] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [pitchIdBeingUpdated, setPitchIdBeingUpdated] = useState(null);
  const [updateStatus, setUpdateStatus] = useState(null);
  const [updateReason, setUpdateReason] = useState("");

  const [followUpModalOpen, setFollowUpModalOpen] = useState(false);
  const [selectedPitchForFollowUp, setSelectedPitchForFollowUp] = useState(null);
  const [isForceFollowUp, setIsForceFollowUp] = useState(false);


  const [initialRefreshDone, setInitialRefreshDone] = useState(false); //flag if we've done the initial refresh

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedPitchForDeletion, setSelectedPitchForDeletion] = useState(null);
  const [restoreDeclaration, setRestoreDeclaration] = useState(false);


  // ========== AUTO-REFRESH ON COMPONENT MOUNT ==========
  useEffect(() => {
    // This will run when the component mounts
    if (!initialRefreshDone) {
      // Refresh all declarations first
      setIsRefreshing(true);
      refreshAllDeclarations()
        .then(result => {
          setMessage(`Global refresh completed: ${result.message || 'Data updated'}`);
          // After global refresh completes, fetch the days ago list
          return fetchDaysAgoList();
        })
        .then((data) => {
          setDaysAgoList(data);
          // Auto-select a day if not already selected
          const newSelectedDays = selectedDays === null 
            ? (data.includes(0) ? 0 : (data.length > 0 ? data[0] : null)) 
            : (data.includes(selectedDays) ? selectedDays : null);
          
          setSelectedDays(newSelectedDays);
          
          // Immediately fetch the pitches data based on the selected day
          if (newSelectedDays !== null) {
            return fetchPitchesByDaysAgo(newSelectedDays)
              .then(pitchData => {
                setPitches(pitchData);
                // Update the all pitches map with this data
                setAllPitchesMap(prev => ({
                  ...prev,
                  [newSelectedDays]: pitchData
                }));
                return data; // Return the original days data for the next then
              });
          }
          return data; // Return the days data for the next then if no day was selected
        })
        .then((data) => {
          // Now fetch data for all other days in the background
          const fetchPromises = data.map(days => {
            // Skip the already fetched days
            if (days === selectedDays) return Promise.resolve();
            
            return fetchPitchesByDaysAgo(days)
              .then(pitchData => {
                setAllPitchesMap(prev => ({
                  ...prev,
                  [days]: pitchData
                }));
              })
              .catch(err => console.error(`Error fetching pitches for ${days} days ago:`, err));
          });
          
          return Promise.all(fetchPromises).then(() => setInitialRefreshDone(true));
        })
        .catch((err) => {
          console.error('Error during initial refresh:', err);
          setMessage(`Error refreshing data: ${err.message || 'Unknown error'}`);
          // Still try to get the days ago list
          fetchDaysAgoList()
            .then((data) => {
              setDaysAgoList(data);
              // Same auto-select logic as above
              if (selectedDays === null) {
                if (data.includes(0)) {
                  setSelectedDays(0);
                } else if (data.length > 0) {
                  setSelectedDays(data[0]);
                }
              } else if (!data.includes(selectedDays)) {
                setSelectedDays(null);
              }
            })
            .catch((daysErr) => console.error('Error fetching days_ago:', daysErr));
        })
        .finally(() => {
          setIsRefreshing(false);
        });
    }
  }, []); // Only runs once on mount



  useEffect(() => {
    if (message) {
      setIsNotificationVisible(true);
      const timer = setTimeout(() => {
        setIsNotificationVisible(false);
        setTimeout(() => {
          setMessage("");
        }, 500);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  useEffect(() => {
    const fetchPitchesForAllDays = async () => {
      const pitchesMap = {};
      for (const days of daysAgoList) {
        try {
          const data = await fetchPitchesByDaysAgo(days);
          pitchesMap[days] = data;
        } catch (error) {
          console.error(`Error fetching pitches for ${days} days ago:`, error);
          pitchesMap[days] = [];
        }
      }
      setAllPitchesMap(pitchesMap);
    };

    if (daysAgoList.length > 0) {
      fetchPitchesForAllDays();
    }
  }, [daysAgoList]);

  useEffect(() => {
    if (searchTerm.trim() !== "") {
      fetchPitchesByCompanyName(searchTerm)
        .then((data) => setPitches(data))
        .catch((err) => console.error("Error fetching global pitches:", err));
    } else if (selectedDays !== null) {
      fetchPitchesByDaysAgo(selectedDays)
        .then((data) => {
          setPitches(data);
          setAllPitchesMap((prev) => ({
            ...prev,
            [selectedDays]: data,
          }));
        })
        .catch((err) => console.error("Error fetching pitches:", err));
    } else {
      setPitches([]);
    }
  }, [searchTerm, selectedDays]);


  // Create a function to fetch all days data
  const fetchAllDaysData = async () => {
    const pitchesMap = {};
    for (const days of daysAgoList) {
      try {
        const data = await fetchPitchesByDaysAgo(days);
        pitchesMap[days] = data;
      } catch (error) {
        console.error(`Error fetching pitches for ${days} days ago:`, error);
        pitchesMap[days] = [];
      }
    }
    setAllPitchesMap(pitchesMap);
    return pitchesMap;
  };

  // Handle refreshing reply status
  const handleRefreshReplies = () => {
    setIsRefreshing(true);
    
    // First refresh all declarations
    refreshAllDeclarations()
      .then((declarationResponse) => {
        console.log('Global refresh response:', declarationResponse);
        // Then refresh reply status
        return refreshReplyStatus();
      })
      .then((response) => {
        console.log('Refresh response:', response);
        setMessage(`Updated reply status for ${response.updated_pitches?.length || 0} pitches`);
        
        // After refresh, fetch data for all days
        return fetchAllDaysData();
      })
      .then((allPitchesData) => {
        if (searchTerm.trim() !== "") {
          return fetchPitchesByCompanyName(searchTerm);
        } else if (selectedDays !== null) {
          const currentDayData = allPitchesData[selectedDays] || [];
          setPitches(currentDayData);
          return [];
        }
        return [];
      })
      .then((data) => {
        if (data.length > 0) {
          setPitches(data);
        }
      })
      .catch((err) => {

        console.error("Detailed refresh error:", err);
        setMessage(
          `Error refreshing reply status: ${err.message || "Unknown error"}`
        );
      })
      .finally(() => {
        setIsRefreshing(false);
      });
  };

  const refetchDaysAgoList = () => {
    fetchDaysAgoList()
      .then((data) => {
        setDaysAgoList(data);
        if (selectedDays !== null && !data.includes(selectedDays)) {
          setSelectedDays(null);
        }
      })
      .catch((err) => console.error("Error fetching days_ago:", err));
  };

  const labelForDaysAgo = (days) => {
    if (days === 0) return "Today";
    if (days === 1) return "Yesterday";
    return `${days} days ago`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("de-DE", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleSendFollowUp = (pitch, force = false) => {
    setSelectedPitchForFollowUp(pitch);
    setIsForceFollowUp(force);
    setFollowUpModalOpen(true);
  };

  const confirmSendFollowUp = () => {
    const pitch = selectedPitchForFollowUp;
    if (!pitch) return;
  
    setLoadingFollowUp(pitch.pitch_id);
  
    sendPitchFollowUp(pitch.pitch_id, isForceFollowUp)
      .then(() => {
        setMessage(`Follow-up sent for ${pitch.company_name}`);
        handleRefreshReplies();
      })
      .catch((error) => {
        setMessage(
          `Error sending follow-up: ${error.message || "Unknown error"}`
        );
      })
      .finally(() => {
        setLoadingFollowUp(null);
        setFollowUpModalOpen(false);
        setSelectedPitchForFollowUp(null);
      });
  };

  const handleMarkClick = (pitchId, newStatus) => {
    setPitchIdBeingUpdated(pitchId);
    setUpdateStatus(newStatus);
    setUpdateReason("");
    setModalOpen(true);
  };

  const handleSubmitReason = () => {
    if (!pitchIdBeingUpdated || !updateStatus) return;

    updatePitchStatus(pitchIdBeingUpdated, {
      status: updateStatus,
      reason: updateReason,
    })
      .then((data) => {
        setMessage(data.message);
        setModalOpen(false);
        setPitchIdBeingUpdated(null);
        setUpdateStatus(null);
        setUpdateReason("");

        if (searchTerm.trim() !== "") {
          fetchPitchesByCompanyName(searchTerm)
            .then((updatedPitches) => {
              setPitches(updatedPitches);
              refetchDaysAgoList();
            })
            .catch(console.error);
        } else if (selectedDays !== null) {
          fetchPitchesByDaysAgo(selectedDays)
            .then((updatedPitches) => {
              setPitches(updatedPitches);
              refetchDaysAgoList();
            })
            .catch(console.error);
        } else {
          refetchDaysAgoList();
        }
      })
      .catch((error) => {
        console.error("Error updating status:", error);
        setMessage(
          `Error updating pitch status: ${error.message || "Unknown error"}`
        );
      });
  };

  const handleCancelUpdate = () => {
    setModalOpen(false);
    setPitchIdBeingUpdated(null);
    setUpdateStatus(null);
    setUpdateReason("");
  };

  const handleDeleteClick = (pitch) => {
    setSelectedPitchForDeletion(pitch);
    setRestoreDeclaration(false);
    setDeleteModalOpen(true);
  };

  const confirmDeletePitch = () => {
    if (!selectedPitchForDeletion) return;
    
    deletePitch(selectedPitchForDeletion.pitch_id, restoreDeclaration)
      .then((response) => {
        setMessage(response.message);
        handleRefreshReplies(); // Refresh the data
      })
      .catch((error) => {
        setMessage(`Error deleting pitch: ${error.message || "Unknown error"}`);
      })
      .finally(() => {
        setDeleteModalOpen(false);
        setSelectedPitchForDeletion(null);
      });
  };

  const containerStyle = {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    position: "relative",
  };

  const mainContentStyle = {
    flexGrow: 1,
  };

  const unreadBadgeStyle = {
    display: "inline-block",
    backgroundColor: "#f44336",
    color: "white",
    fontWeight: "bold",
    padding: "2px 8px",
    borderRadius: "12px",
    fontSize: "0.8rem",
    marginLeft: "10px",
  };

  return (
    <div>
      <h2>Pending Pitches</h2>

      {message && (
        <div className="message"
          style={{
            opacity: isNotificationVisible ? 1 : 0,
          }}
        >
          {message}
        </div>
      )}
      
      {!initialRefreshDone && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem',
          backgroundColor: '#f9f9f9',
          borderRadius: '4px',
          margin: '1rem 0'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid rgba(0, 0, 0, 0.1)',
            borderRadius: '50%',
            borderTop: '4px solid #2196F3',
            animation: 'spin 1s linear infinite',
            marginBottom: '1rem'
          }}></div>
          <p>Refreshing data to ensure you have the latest information...</p>
        </div>
      )}

      {initialRefreshDone && (
        <>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "1rem",
          }}
        >
          <button
            className="button-hide"
            onClick={() => setShowSidebar(!showSidebar)}
          >
            {showSidebar ? (
              <>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
                Hide Navigation
              </>
            ) : (
              <>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16m-7 6h7"
                  />
                </svg>
                Show Navigation
              </>
            )}
          </button>

          <button
            className="button-hide"
            onClick={handleRefreshReplies}
            disabled={isRefreshing}
          >
            {isRefreshing ? (
              <>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ animation: "spin 1s linear infinite" }}
                >
                  <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2" />
                </svg>
                <span className="dots">Checking</span>
              </>
            ) : (
              <>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2" />
                </svg>
                Check Replies
              </>
            )}
          </button>
        </div>

        <div style={containerStyle}>
          {showSidebar && (
            <div className="sidebar">
              <h4 className="sidebar-heading">Open Pitches</h4>
              {daysAgoList.length === 0 ? (
                <p style={{ textAlign: "center", color: "#777" }}>
                  No pending pitches found.
                </p>
              ) : (
                <ul>
                  {daysAgoList.map((days) => {
                    const pitchesForDay = allPitchesMap[days] || [];
                    const unreadCount = pitchesForDay.filter(
                      (p) => p.has_unread_reply
                    ).length;

                    return (
                      <li
                        key={days}
                        className={`date-item ${selectedDays === days ? "active" : ""}`}
                        onClick={() => setSelectedDays(days)}
                      >
                        {labelForDaysAgo(days)}
                        {unreadCount > 0 && (
                          <span style={unreadBadgeStyle}>{unreadCount}</span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}

          <div style={mainContentStyle}>
            <div
              style={{
                background: "#fff",
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                padding: "1.5rem",
                marginBottom: "1.5rem",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "1rem",
                  marginBottom: "1rem",
                  alignItems: "center",
                  borderBottom: "1px solid #f0f0f0",
                  paddingBottom: "1rem",
                }}
              >
                <div>
                  <label style={{ marginRight: "8px" }}>Global Search:</label>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Enter company name..."
                    style={{
                      width: "300px",
                      maxWidth: "100%",
                      padding: "8px",
                      border: "1px solid #d9d9d9",
                      borderRadius: "4px",
                    }}
                  />
                  {searchTerm.trim() !== "" && (
                    <span
                      style={{
                        marginLeft: "10px",
                        fontStyle: "italic",
                        color: "#888",
                      }}
                    >
                      (Global search active)
                    </span>
                  )}
                </div>
              </div>

              {selectedDays !== null || searchTerm.trim() !== "" ? (
                <>
                  {searchTerm.trim() === "" && (
                    <p style={{ marginBottom: "1rem", fontSize: "1rem" }}>
                      Showing pending pitches sent{" "}
                      <strong>{labelForDaysAgo(selectedDays)}</strong>
                      {pitches.filter((p) => p.has_unread_reply).length > 0 && (
                        <span
                          style={{
                            marginLeft: "10px",
                            fontWeight: "bold",
                            color: "#f44336",
                          }}
                        >
                          ({pitches.filter((p) => p.has_unread_reply).length} with
                          new replies)
                        </span>
                      )}
                    </p>
                  )}

                  {pitches.length === 0 ? (
                    <div
                      style={{
                        padding: "2rem",
                        textAlign: "center",
                        backgroundColor: "#f5f5f5",
                        borderRadius: "4px",
                        marginTop: "1rem",
                      }}
                    >
                      No pitches to display for this range / search.
                    </div>
                  ) : (
                    <div style={{ overflowX: "auto" }}>
                      <table>
                        <thead>
                          <tr
                            style={{
                              backgroundColor: "#fafafa",
                              borderBottom: "2px solid #f0f0f0",
                            }}
                          >
                            <th
                              style={{ padding: "12px 16px", textAlign: "left" }}
                            >
                              Company
                            </th>
                            <th
                              style={{ padding: "12px 16px", textAlign: "left" }}
                            >
                              Sent At
                            </th>
                            <th
                              style={{ padding: "12px 16px", textAlign: "left" }}
                            >
                              Agent Name
                            </th>
                            <th
                              style={{ padding: "12px 16px", textAlign: "left" }}
                            >
                              Email Thread
                            </th>
                            <th
                              style={{ padding: "12px 16px", textAlign: "left" }}
                            >
                              Last Reply
                            </th>
                            <th
                              style={{ padding: "12px 16px", textAlign: "left" }}
                            >
                              Follow-up
                            </th>
                            <th
                              style={{ padding: "12px 16px", textAlign: "left" }}
                            >
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {pitches.map((pitch, index) => {
                            const sentDateObj = new Date(pitch.sent_at);
                            const diffTime =
                              new Date().setHours(0, 0, 0, 0) -
                              new Date(
                                sentDateObj.getFullYear(),
                                sentDateObj.getMonth(),
                                sentDateObj.getDate()
                              );
                            const diffDays = Math.floor(
                              diffTime / (1000 * 60 * 60 * 24)
                            );

                            const isEligibleForFollowUp = diffDays >= 7;
                            const hasFollowUp = !!pitch.last_follow_up;
                            const hasReply = !!pitch.last_reply_at;

                            return (
                              <tr
                                key={pitch.pitch_id || index}
                                style={{
                                  borderBottom: "1px solid #f0f0f0",
                                  backgroundColor: pitch.has_unread_reply
                                    ? "rgba(25, 118, 210, 0.07)"
                                    : index % 2 === 0
                                    ? "#ffffff"
                                    : "#fafafa",
                                }}
                              >
                                <td style={{ padding: "12px 16px" }}>
                                  {pitch.company_name}
                                  {pitch.has_unread_reply && (
                                    <span style={unreadBadgeStyle}>NEW</span>
                                  )}
                                </td>
                                <td style={{ padding: "12px 16px" }}>
                                  {new Date(pitch.sent_at)
                                    .toISOString()
                                    .slice(0, 10)}
                                </td>
                                <td style={{ padding: "12px 16px" }}>
                                  {pitch.agent_name}
                                </td>
                                <td style={{ padding: "12px 16px" }}>
                                  <a
                                    href={pitch.email_thread}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    View Email Thread
                                  </a>
                                </td>
                                <td style={{ padding: "12px 16px" }}>
                                  {pitch.last_reply_at
                                    ? formatDate(pitch.last_reply_at)
                                    : "No replies yet"}
                                </td>
                                <td style={{ padding: "12px 16px" }}>
                                  {hasFollowUp ? (
                                    <button
                                      style={{
                                        padding: "6px 12px",
                                        backgroundColor: "#999",
                                        color: "white",
                                        border: "none",
                                        borderRadius: "4px",
                                        cursor: "not-allowed",
                                        opacity: 0.7,
                                      }}
                                      disabled
                                      title={`Follow-up already sent on ${formatDate(
                                        pitch.last_follow_up
                                      )}`}
                                    >
                                      Follow-up Sent
                                    </button>
                                  ) : isEligibleForFollowUp ? (
                                    !hasReply ? (
                                      <button
                                        className="follow-up-button"
                                        style={{
                                          position: "relative",
                                          cursor: loadingFollowUp === pitch.pitch_id ? "wait" : "pointer",
                                          opacity: loadingFollowUp === pitch.pitch_id ? 0.7 : 1,
                                        }}
                                        onClick={() => handleSendFollowUp(pitch, false)}
                                        disabled={loadingFollowUp === pitch.pitch_id}
                                      >
                                        {loadingFollowUp === pitch.pitch_id ? "Sending..." : "Send Follow-up"}
                                      </button>
                                    ) : (
                                      <button
                                        style={{
                                          position: "relative",
                                          padding: "6px 12px",
                                          backgroundColor: "#FF9800",
                                          color: "white",
                                          border: "none",
                                          borderRadius: "4px",
                                          cursor:
                                            loadingFollowUp === pitch.pitch_id
                                              ? "wait"
                                              : "pointer",
                                          opacity:
                                            loadingFollowUp === pitch.pitch_id
                                              ? 0.7
                                              : 1,
                                        }}
                                        onClick={() =>
                                          handleSendFollowUp(pitch, true)
                                        }
                                        disabled={
                                          loadingFollowUp === pitch.pitch_id
                                        }
                                        title="Force follow-up despite having received a reply"
                                      >
                                        {loadingFollowUp === pitch.pitch_id
                                          ? "Sending..."
                                          : "Force Follow-up"}
                                      </button>
                                    )
                                  ) : (
                                    <button
                                      style={{
                                        padding: "6px 12px",
                                        backgroundColor: "#9E9E9E",
                                        color: "white",
                                        border: "none",
                                        borderRadius: "4px",
                                        cursor: "not-allowed",
                                        opacity: 0.6,
                                      }}
                                      disabled
                                      title={`Eligible for follow-up in ${
                                        7 - diffDays
                                      } day(s)`}
                                    >
                                      Wait {7 - diffDays} more day
                                      {7 - diffDays !== 1 ? "s" : ""}
                                    </button>
                                  )}
                                </td>
                                <td style={{ padding: '12px 16px' }}>
                                  <div style={{ display: 'flex', gap: '8px' }}>
                                    <button
                                      className="action-button button-won"
                                      onClick={() => handleMarkClick(pitch.pitch_id, "Won")}
                                    >
                                      Won
                                    </button>
                                    <button
                                      className="action-button button-lost"
                                      onClick={() => handleMarkClick(pitch.pitch_id, "Lost")}
                                    >
                                      Lost
                                    </button>
                                    <button
                                      className="action-button button-delete-pitch"
                                      onClick={() => handleDeleteClick(pitch)}
                                      title="Delete pitch"
                                    >
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        strokeWidth="1.5"
                                        stroke="currentColor"
                                        width="16"
                                        height="16"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                                        />
                                      </svg>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              ) : (
                <div
                  style={{
                    padding: "2rem",
                    textAlign: "center",
                    backgroundColor: "#f5f5f5",
                    borderRadius: "4px",
                    marginTop: "1rem",
                  }}
                >
                  Please select a time range from the navigation.
                </div>
              )}
            </div>
          </div>
        </div>
        </>
      )}

      {modalOpen && (
        <div className="popup-overlay">
          <div
            className={`popup-content ${updateStatus === 'Won' ? 'won' : updateStatus === 'Lost' ? 'lost' : ''}`}
          >
            <h3>
              Mark Pitch as <strong>{updateStatus}</strong>
            </h3>
            <textarea
              value={updateReason}
              onChange={(e) => setUpdateReason(e.target.value)}
              rows={3}
              style={{ width: "100%", marginBottom: "16px", padding: "8px" }}
              placeholder="Enter reason (optional)..."
            />
            <div className="popup-actions">
              <button
                onClick={handleSubmitReason}
                className="button-accept"
              >
                Submit
              </button>
              <button
                onClick={handleCancelUpdate}
                className="button-cancel"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {followUpModalOpen && selectedPitchForFollowUp && (
        <div className="popup-overlay">
          <div className="popup-content">
            <h3>
              {isForceFollowUp ? "Force" : "Send"} Follow-up?
            </h3>
            <p>
              Are you sure you want to {isForceFollowUp ? "force " : ""}send a follow-up email to <strong>{selectedPitchForFollowUp.company_name}</strong>?
            </p>
            <div className="popup-actions">
              <button className="button-accept" onClick={confirmSendFollowUp}>
                Yes, Send
              </button>
              <button
                className="button-cancel"
                onClick={() => setFollowUpModalOpen(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {deleteModalOpen && selectedPitchForDeletion && (
        <div className="popup-overlay">
          <div className="popup-content">
            <h3 style={{color: '#cf1322'}}>Delete Pitch</h3>
            <p>
              Are you sure you want to delete the pitch for <strong>{selectedPitchForDeletion.company_name}</strong>?
            </p>
            <div className="checkbox-section">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={restoreDeclaration}
                  onChange={() => setRestoreDeclaration(!restoreDeclaration)}
                />
                Restore declaration for new pitch
              </label>
              <p className="popup-checkbox-description">
                {restoreDeclaration 
                  ? "This will delete the pitch and reset the declaration, making it available to send a new pitch."
                  : "This will delete the pitch but keep the declaration marked as sent."}
              </p>
            </div>
            <div className="popup-actions">
              <button className="button-delete" onClick={confirmDeletePitch}>
                Delete
              </button>
              <button
                className="button-cancel"
                onClick={() => setDeleteModalOpen(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TablePitches;